<template>
    <v-dialog :model-value="modelValue" persistent width="520"
              @update:model-value="$emit('update:modelValue', $event)">
        <v-card>
            <v-card-title class="d-flex justify-space-between align-center">
                <span>Set engine hours</span>
                <v-btn icon="mdi-close" variant="text" density="compact" aria-label="Close" @click="close"/>
            </v-card-title>

            <v-form v-model="valid" @submit.prevent="save">
                <v-card-text>
                    <v-alert v-if="errorMessage" class="mb-3" density="compact" type="error" variant="tonal">
                        {{ errorMessage }}
                    </v-alert>
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="form.hours" :error-messages="errors.hours" :rules="hoursRules"
                                          density="compact" label="Engine hours *" min="0" step="0.1" suffix="h"
                                          type="number"/>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="form.reason" :error-messages="errors.reason" :items="reasons"
                                      density="compact" item-title="label" item-value="value" label="Reason *"/>
                        </v-col>
                        <v-col cols="12">
                            <v-text-field v-model="form.effective_at" :error-messages="errors.effective_at"
                                          :max="nowInput" density="compact" hint="Leave empty for now"
                                          label="Reading taken at" persistent-hint type="datetime-local"/>
                        </v-col>
                        <v-col cols="12">
                            <v-textarea v-model="form.note" :error-messages="errors.note" counter="255"
                                        density="compact" label="Note" maxlength="255" rows="2"/>
                        </v-col>
                    </v-row>
                </v-card-text>

                <v-card-actions class="px-4 pb-3 d-flex justify-end">
                    <v-btn variant="text" @click="close">Cancel</v-btn>
                    <v-btn :disabled="!valid || saving" :loading="saving" color="primary" type="submit">Save</v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {reactive, ref, watch} from "vue";
import {send} from "@/composables/apiClient";
import {companyTimezone, toZonedInput, zonedInputToUtcIso} from "@/composables/useTimezone";
import {useSnackbarStore} from "@/stores/snackBarStore";

const props = defineProps({
    modelValue: Boolean,
    assetId: {type: [String, Number], required: true},
    currentHours: {type: [Number, String], default: null},
});
const emit = defineEmits(["update:modelValue", "saved"]);
const {showSnackbar} = useSnackbarStore();

const reasons = [
    {value: 'manual', label: 'Manual reading'},
    {value: 'ecu_replaced', label: 'Engine ECU replaced'},
];
const hoursRules = [
    v => (v !== null && v !== '') || 'Engine hours are required',
    v => Number(v) >= 0 || 'Enter zero or more hours',
];

const tz = companyTimezone();
const valid = ref(false);
const saving = ref(false);
const errorMessage = ref('');
const errors = ref({});
const nowInput = ref('');
const form = reactive({hours: null, reason: 'manual', effective_at: '', note: ''});

watch(() => props.modelValue, (open) => {
    if (!open) return;
    form.hours = props.currentHours != null ? Number(props.currentHours) : null;
    form.reason = 'manual';
    form.effective_at = '';
    form.note = '';
    errors.value = {};
    errorMessage.value = '';
    nowInput.value = toZonedInput(new Date().toISOString(), tz);
}, {immediate: true});

const close = () => emit("update:modelValue", false);

const save = () => {
    if (!valid.value) return;
    errors.value = {};
    errorMessage.value = '';

    const body = {hours: Number(form.hours), reason: form.reason};
    if (form.effective_at) {
        body.effective_at = zonedInputToUtcIso(form.effective_at, tz);
    }
    if (form.note) body.note = form.note;

    saving.value = true;
    send('post', `assets/${props.assetId}/engine-hours`, body)
        .then((response) => {
            showSnackbar(response.data.message ?? 'Engine hours saved');
            emit("saved", response.data.data);
            close();
        })
        .catch((error) => {
            // Form validation puts field errors under errors, the API's own checks under data
            errors.value = error.response?.data?.errors ?? error.response?.data?.data ?? {};
            errorMessage.value = error.response?.data?.message ?? 'Engine hours could not be saved';
        })
        .finally(() => saving.value = false);
};
</script>
